import { combineReducers } from 'redux';
import { ActionType } from 'typesafe-actions';
import * as models from '../../models';
import * as actions from './actions';
import * as constants from './constants';

export interface AiLabStateShape {
    aiToolConfiguration?: models.AiToolConfiguration;
    aiToolsDD: models.DropdownListResponse;
    isAiToolConfigurationUpdating: boolean;
    isLoadingClaimToolsDD: boolean;
    isPublishingTool: boolean;
    publishToolError?: string;        
    catalogTools: models.AiToolCatlogResponse;
    isLoadingCatalog: boolean;
    catalogError?: string;
    updateError?: string; 
}

export const aiLabDefaultState: AiLabStateShape = {    
    aiToolConfiguration: undefined,
    aiToolsDD: models.defaultResponse, 
    isAiToolConfigurationUpdating: false,
    isLoadingClaimToolsDD: false,
    isPublishingTool: false,
    publishToolError: undefined,
    catalogTools: models.defaultResponse,
    isLoadingCatalog: false,
    catalogError: undefined, 
    updateError: undefined,
};

export interface AiExecutionState {
    isExecuting: boolean;
    executionLogId: number; 
    executionStatus: 'Initiating' | 'PendingInput' | 'Queued' | 'Processing' | 'Complete' | 'Failed';
    errorMessage: string;
    outputHtml: string;
    requiredFiles: models.AiRequiredInput[];
    uploadedFiles: { [slotId: string]: string };
    bundleMatch: RuntimeBundleMatchState;
}

export interface RuntimeBundleMatchState {
    isMatching: boolean;
    result?: models.AiRuntimeBundleMatch;
    error?: string;
}

const defaultBundleMatch: RuntimeBundleMatchState = {
    isMatching: false,
    result: undefined,
    error: undefined
};

const initialState: AiExecutionState = {
    isExecuting: false,
    executionLogId: 0,
    executionStatus: 'Initiating',
    errorMessage: '',
    outputHtml: '',
    requiredFiles: [],
    uploadedFiles: {},
    bundleMatch: defaultBundleMatch
};

export interface State {
    aiLab: AiLabStateShape;
    aiExecution: AiExecutionState;
}

export type Action = ActionType<typeof actions>;

export default combineReducers<State, Action>({
    aiLab: (state = aiLabDefaultState, action) => {
        switch (action.type) {
            case constants.REQUEST_AI_TOOL_PUBLISH:
                return {
                    ...state, 
                    isPublishingTool: true,
                };
            case constants.REQUEST_AI_TOOL_PUBLISH_SUCCESS:
                return {
                    ...state, 
                    isPublishingTool: false,
                };
            case constants.REQUEST_AI_TOOL_PUBLISH_FAILURE:
                return {
                    ...state, 
                    isPublishingTool: false,
                    publishToolError: action.payload.error,
                };
            case constants.REQUEST_CLAIM_LEVEL_AI_TOOLS:
                return {
                    ...state, 
                    isLoadingClaimToolsDD: true,
                };
            case constants.RECEIVE_CLAIM_LEVEL_AI_TOOLS:
                return {
                    ...state, 
                    isLoadingClaimToolsDD: false,
                    aiToolsDD: action.payload.toolsDD,
                };
            case constants.REQUEST_FETCH_CATALOG_TOOLS:
                return {
                    ...state, 
                    isLoadingCatalog: true,
                    catalogError: undefined
                };
            case constants.REQUEST_FETCH_CATALOG_TOOLS_SUCCESS:
                return {
                    ...state, 
                    isLoadingCatalog: false,
                    catalogTools: action.payload.response
                };
            case constants.REQUEST_FETCH_CATALOG_TOOLS_FAILURE:
                return {
                    ...state, 
                    isLoadingCatalog: false,
                    catalogError: action.payload.error
                };
            case constants.REQUEST_AI_TOOL_CONFIGURATION:
                 return {
                    ...state            
                };
            case constants.RECEIVE_AI_TOOL_CONFIGURATION:
                return {
                    ...state, 
                    aiToolConfiguration: action.payload.response
                };          
            case constants.UPDATE_AI_TOOL_CONFIGURATION:
                return {
                    ...state, 
                    isAiToolConfigurationUpdating: true,                   
                };    
            case constants.UPDATE_AI_TOOL_CONFIGURATION_SUCCESS:
                return {
                    ...state,
                    isAiToolConfigurationUpdating: false,
                };
            case constants.UPDATE_AI_TOOL_CONFIGURATION_FAILURE:
                return {
                    ...state, 
                    isAiToolConfigurationUpdating: false, 
                    updateError: action.payload.error,
                };
            case constants.CREATE_AI_TOOL_CONFIGURATION:
                return {
                    ...state, 
                };
            case constants.CREATE_AI_TOOL_CONFIGURATION_SUCCESS:
                return {
                    ...state,                 
                };
            default: 
                return state; 
        }
    },
    aiExecution: (state = initialState, action) => {
        switch (action.type) {
            case constants.REQUEST_EXECUTE_AI_TOOL:
                return {
                    ...state,
                    isExecuting: true,
                    executionLogId: 0,
                    executionStatus: 'Initiating',
                    errorMessage: '',
                    outputHtml: '',
                    requiredFiles: [],
                    uploadedFiles: {},
                    bundleMatch: defaultBundleMatch
                };

            case constants.REQUEST_MATCH_RUNTIME_BUNDLE:
                return {
                    ...state,
                    bundleMatch: { isMatching: true, result: undefined, error: undefined }
                };

            case constants.REQUEST_MATCH_RUNTIME_BUNDLE_SUCCESS:
                return {
                    ...state,
                    bundleMatch: { isMatching: false, result: action.payload.result, error: undefined }
                };

            case constants.REQUEST_MATCH_RUNTIME_BUNDLE_FAILURE:
                return {
                    ...state,
                    bundleMatch: { isMatching: false, result: undefined, error: action.payload.error }
                };

            case constants.CLEAR_RUNTIME_BUNDLE_MATCH:
                return {
                    ...state,
                    bundleMatch: defaultBundleMatch
                };
            
            case constants.REQUEST_EXECUTE_AI_TOOL_SUCCESS:
            case constants.SUBMIT_AI_TOOL_INPUT_SUCCESS: {
                const responseData = action.payload.responseData;

                // 1. Asynchronous Queue Path (Expected for long jobs/large files)
                if (responseData.status === 'Queued') {
                    return {
                        ...state,
                        isExecuting: true,
                        executionLogId: responseData.executionLogId || 0,
                        executionStatus: 'Queued'
                    };
                }

                // 2. Direct Sync Completion Path (if returned synchronously)
                if (responseData.status === 'Complete') {
                    return {
                        ...state,
                        isExecuting: false,
                        executionStatus: 'Complete',
                        outputHtml: responseData.outputHtml || responseData.formattedHtml || responseData.finalHtml || ''
                    };
                }                

                // 3. Pending Additional Input / Uploads Path
                return {
                    ...state,
                    isExecuting: false,
                    executionStatus: 'PendingInput',
                    requiredFiles: responseData.requiredInputs?.files || []
                };
            }

            case constants.SET_AI_TOOL_EXECUTION_COMPLETE:
                return {
                    ...state,
                    isExecuting: false,
                    executionStatus: 'Complete',
                    outputHtml: action.payload.outputHtml
                };

            case constants.SET_AI_TOOL_EXECUTION_FAILED:
                return {
                    ...state,
                    isExecuting: false,
                    executionStatus: 'Failed',
                    errorMessage: action.payload.errorMessage
                };
            
            case constants.SUBMIT_AI_TOOL_INPUT_FAILURE: 
            case constants.REQUEST_EXECUTE_AI_TOOL_FAILURE:
                return {
                    ...state,
                    isExecuting: false,
                    executionStatus: 'Failed',
                    errorMessage: action.payload.error
                };

            case constants.SUBMIT_AI_TOOL_INPUT:
                return {
                    ...state,
                    isExecuting: true,
                    executionStatus: 'Processing'
                };

            case constants.RESET_AI_TOOL_EXECUTION:
                return initialState;

            default:
                return state;
        }
    }
});